import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageDetect } from '@/hooks/useLanguageDetect';

export default function Navbar() {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguageDetect();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: t('navHome') },
    { to: '/search', label: t('navHealth') },
    { to: '/sugaraway-v4', label: '醣溜溜 V4' },
    { to: '/about', label: t('navAbout') },
  ];

  const toggleLanguage = () => {
    const newLang = language === 'zh-Hans' ? 'zh-Hant' : 'zh-Hans';
    setLanguage(newLang);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 shadow-sm backdrop-blur-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 py-3 flex items-center justify-between">
        <Link
          to="/"
          className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors ${
            isScrolled ? 'text-foreground-900' : 'text-white'
          }`}
        >
          {t('siteName')}
        </Link>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  location.pathname === link.to
                    ? isScrolled
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-white/20 text-white'
                    : isScrolled
                      ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <button
            onClick={toggleLanguage}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              isScrolled
                ? 'bg-background-100 text-foreground-600 hover:bg-background-200'
                : 'bg-white/15 text-white hover:bg-white/25'
            }`}
            title={language === 'zh-Hans' ? '切換至繁體中文' : '切换至简体中文'}
          >
            <i className="ri-translate-2 text-xs" />
            <span>{language === 'zh-Hans' ? t('switchToTraditional') : t('switchToSimplified')}</span>
          </button>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg cursor-pointer transition-colors ${
              isScrolled ? 'text-foreground-700' : 'text-white'
            }`}
          >
            <i className={mobileMenuOpen ? 'ri-close-line text-lg' : 'ri-menu-line text-lg'} />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60 px-4 py-3 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === link.to
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
